import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  RefreshCw,
  HelpCircle,
  Globe,
  LogOut,
  FileText,
  MessageSquare,
  User,
  Activity,
  ChevronDown,
  Menu,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Logo } from "@/components/Logo";
import { useLanguage } from "@/i18n/LanguageContext";
import { clearAuth, getAuthUser } from "@/lib/auth";
import { cn } from "@/lib/utils";
import type { UserRead } from "@/types";

interface NavbarProps {
  onResetChat?: () => void;
  showReset?: boolean;
  publicMode?: boolean;
}

export const Navbar: React.FC<NavbarProps> = ({
  onResetChat,
  showReset = false,
  publicMode = false,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { language, toggleLanguage, t } = useLanguage();
  const [profileOpen, setProfileOpen] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const profileRef = useRef<HTMLDivElement>(null);
  const user = getAuthUser<UserRead>();
  const displayName = publicMode ? "MOH" : user?.display_name || user?.email || t("header.title");

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (profileRef.current && !profileRef.current.contains(event.target as Node)) {
        setProfileOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    setProfileOpen(false);
    setMobileNavOpen(false);
  }, [location.pathname]);

  const handleLogout = () => {
    clearAuth();
    navigate("/login", { replace: true });
  };

  const helpPath = publicMode ? "/embed/support" : "/support";

  const navItems = [
    {
      label: t("btn.chat"),
      path: publicMode ? "/embed" : "/",
      icon: MessageSquare,
    },
    {
      label: t("btn.dash"),
      path: publicMode ? "/embed/anomalies" : "/anomalies",
      icon: Activity,
    },
    {
      label: t("btn.reports"),
      path: publicMode ? "/embed/report-generator" : "/report-generator",
      icon: FileText,
    },
    {
      label: t("btn.help"),
      path: helpPath,
      icon: HelpCircle,
    },
  ];

  return (
    <header className="sticky top-0 z-50 border-b border-primary-100/30 bg-white/85 px-3 py-2 shadow-soft backdrop-blur-xl sm:px-4">
      <div className="flex min-w-0 items-center justify-between gap-2">
      <div className="flex min-w-0 items-center gap-3 lg:gap-6">
        <div
          className="flex min-w-0 cursor-pointer items-center gap-2 sm:gap-3"
          onClick={() => navigate(publicMode ? "/embed" : "/")}
        >
          <Logo size={38} />
          <div className="hidden min-w-0 sm:block">
            <h1 className="truncate text-xs font-bold leading-tight text-slate-900">
              {t("header.title")}
            </h1>
            <p className="truncate text-[10px] font-medium text-slate-400">
              {t("header.subtitle")}
            </p>
          </div>
        </div>

        <nav className="hidden items-center gap-1 md:flex">
          {navItems.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <button
                key={item.path}
                onClick={() => navigate(item.path)}
                className={cn(
                  "flex items-center gap-1.5 px-3 py-1.5 rounded-3xl text-xs font-medium transition-all duration-200",
                  isActive
                    ? "bg-primary-50 text-primary-700 shadow-soft"
                    : "text-slate-500 hover:text-slate-900 hover:bg-slate-100"
                )}
              >
                <item.icon className={cn("w-3.5 h-3.5", isActive ? "text-primary-500" : "text-slate-400")} />
                {item.label}
              </button>
            );
          })}
        </nav>
      </div>

      <div className="flex shrink-0 items-center gap-1.5 sm:gap-2.5">
        <button
          type="button"
          onClick={() => setMobileNavOpen((prev) => !prev)}
          className="flex h-8 w-8 items-center justify-center rounded-3xl border border-primary-200/50 bg-white text-primary-600 shadow-soft transition-all hover:bg-primary-50 md:hidden"
          aria-label="Toggle navigation"
          aria-expanded={mobileNavOpen}
        >
          {mobileNavOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
        </button>

        <button
          type="button"
          onClick={toggleLanguage}
          className="flex items-center gap-1.5 rounded-3xl border border-primary-200/50 bg-white px-2.5 py-1.5 text-xs font-medium text-primary-600 shadow-soft transition-all duration-200 hover:bg-primary-50 sm:px-3"
          title={language === "en" ? "Switch to Amharic" : "Switch to English"}
        >
          <Globe className="w-3 h-3 text-slate-400" />
          <span>{language === "en" ? t("header.lang.en") : t("header.lang.am")}</span>
        </button>

        {showReset && onResetChat && (
          <Button
            variant="ghost"
            onClick={onResetChat}
            className="flex h-7 items-center gap-1.5 text-xs"
          >
            <RefreshCw className="w-3 h-3" />
            <span className="hidden sm:inline">{t("btn.newChat")}</span>
          </Button>
        )}

        <div ref={profileRef} className="relative">
          <button
            type="button"
            onClick={() => setProfileOpen((prev) => !prev)}
            className="flex h-8 items-center gap-1.5 rounded-3xl border border-primary-200/50 bg-white px-2 text-xs font-medium text-primary-700 shadow-soft transition-colors hover:bg-primary-50 sm:px-2.5"
            aria-haspopup="true"
            aria-expanded={profileOpen}
            title="Profile"
          >
                  <span className="flex h-5 w-5 items-center justify-center rounded-full bg-primary-100 text-primary-600">
              <User className="h-3 w-3" />
            </span>
            <span className="hidden max-w-[9rem] truncate sm:inline">{displayName}</span>
            <ChevronDown className="h-3 w-3 text-slate-400" />
          </button>

          {profileOpen && (
            <div className="absolute right-0 z-20 mt-2 w-[min(16rem,calc(100vw-1.5rem))] rounded-3xl border border-slate-200 bg-white p-3 shadow-ambient">
              <div className="border-b border-slate-100 pb-3">
                <div className="text-sm font-semibold text-slate-900">
                  {displayName}
                </div>
                {!publicMode && (
                  <div className="text-xs text-slate-500">
                    {user?.email || "No email on file"}
                  </div>
                )}
              </div>

              {!publicMode && (
                <button
                  type="button"
                  onClick={handleLogout}
                  className="mt-3 flex w-full items-center gap-2 rounded-2xl px-3 py-2 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-50"
                >
                  <LogOut className="h-4 w-4" />
                  {t("btn.logout")}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
      </div>

      {mobileNavOpen && (
        <nav className="mt-2 grid grid-cols-2 gap-1 rounded-2xl border border-primary-100/50 bg-white/90 p-1 shadow-soft md:hidden">
          {navItems.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <button
                key={item.path}
                type="button"
                onClick={() => navigate(item.path)}
                className={cn(
                  "flex h-9 min-w-0 items-center justify-center gap-1.5 rounded-xl px-2 text-[11px] font-medium transition-all",
                  isActive
                    ? "bg-primary-500 text-white shadow-soft"
                    : "bg-primary-50/60 text-primary-700 hover:bg-primary-100",
                )}
              >
                <item.icon className={cn("h-3.5 w-3.5 shrink-0", isActive ? "text-white" : "text-primary-400")} />
                <span className="truncate">{item.label}</span>
              </button>
            );
          })}
        </nav>
      )}
    </header>
  );
};
